/**
 * Компонент выбора поля для фильтрации
 */

import { Select } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";

import { ZFieldMeta, ZFilterableField } from "../../types";
import { ControlWithError } from "../ControlWithError/ControlWithError";
import * as styles from "./FieldSelector.module.less";

const fieldTypeRegistry: Record<ZFieldMeta["fieldType"], string> = {
  SYSTEM: "Системные поля",
  SPEC: "Поля спецификации",
};

const fieldTypeOrder: ZFieldMeta["fieldType"][] = ["SYSTEM", "SPEC"];

interface PropsFieldSelector {
  /** Список доступных полей */
  fields: ZFilterableField[];

  /** Выбранное поле (tag) */
  value: string | null;

  /** Callback при выборе поля */
  onChange: (
    fieldTag: string | null,
    field: ZFilterableField | undefined,
  ) => void;

  /** Placeholder */
  placeholder?: string;

  /** Disabled состояние */
  disabled?: boolean;

  /** Текст ошибки */
  errorText?: string;
}

/**
 * Селектор поля с поиском и группировкой
 */
export const FieldSelector: React.FC<PropsFieldSelector> = observer(
  ({
    fields,
    value,
    onChange,
    placeholder = "Выбор атрибута",
    disabled,
    errorText,
  }) => {
    const handleChange = (fieldTag: string | undefined) => {
      if (!fieldTag) {
        onChange(null, undefined);
        return;
      }

      const field = fields.find((f) => f.tag === fieldTag);
      onChange(fieldTag, field);
    };

    const options = React.useMemo(
      () =>
        fieldTypeOrder
          .map((fieldType) => ({
            label: fieldTypeRegistry[fieldType],
            options: fields
              .filter((field) => field.fieldMeta.fieldType === fieldType)
              .map((field) => ({
                label: field.label,
                value: field.tag,
              })),
          }))
          .filter((group) => group.options.length > 0),
      [fields],
    );

    return (
      <ControlWithError errorText={errorText}>
        <Select
          className={styles.fieldSelector}
          value={value || undefined}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          status={errorText ? "error" : undefined}
          showSearch
          optionFilterProp="label"
          options={options}
          allowClear
        />
      </ControlWithError>
    );
  },
);
